/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import React from 'react'
import { TimePeriod } from 'src/context/MyScheduleContext/types'

interface TimePeriodFilterProps {
  selected: TimePeriod
  onSelect: (period: TimePeriod) => void
  disabled?: boolean
}

/**
 * TimePeriodFilter component
 * Displays Today/Upcoming/Past tabs for filtering games
 * Pixel perfect match to Figma design
 */
const TimePeriodFilter: React.FC<TimePeriodFilterProps> = ({
  selected,
  onSelect,
  disabled = false,
}) => {
  const tabs: { value: TimePeriod; label: string }[] = [
    { value: 'PAST', label: 'Past' },
    { value: 'TODAY', label: 'Today' },
    { value: 'UPCOMING', label: 'Upcoming' },
  ]

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: 0px;
        background-color: transparent;
        border: 1px solid ${colors.SECONDARY_200};
        border-radius: 8px;
        height: 40px;
        opacity: ${disabled ? 0.5 : 1};
        transition: opacity 0.2s ease;
        max-width: 300px;
        @media (max-width: 768px) {
          max-width: 100%;
          width: 100%;
        }
      `}
    >
      {tabs.map((tab, index) => {
        const isActive = selected === tab.value
        const isFirst = index === 0
        const isLast = index === tabs.length - 1

        return (
          <button
            key={tab.value}
            type='button'
            disabled={disabled}
            onClick={() => !disabled && onSelect(tab.value)}
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              width: 98px;
              height: 40px;
              border-radius: ${isFirst
                ? '8px 0 0 8px'
                : isLast
                ? '0 8px 8px 0'
                : '0'};
              background-color: ${isActive
                ? colors.SECONDARY_300
                : 'transparent'};
              border: none;
              cursor: ${disabled ? 'not-allowed' : 'pointer'};
              transition: all 0.2s ease;
              font-size: 14px;
              font-weight: ${isActive ? 700 : 400};
              line-height: 20.3px;
              color: ${colors.NEUTRAL_0};
              @media (max-width: 768px) {
                width: 100%;
              }

              ${!disabled &&
              css`
                &:hover {
                  background-color: ${isActive
                    ? colors.SECONDARY_300
                    : colors.SECONDARY_200};
                }

                &:active {
                  transform: scale(0.98);
                }
                @media (max-width: 768px) {
                  width: 100%;
                }
              `}
            `}
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}

export default TimePeriodFilter
